import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Button, Checkbox, Radio } from 'antd';

import { verifyFeatureAccess } from '../../global/featureFlags';
import { captureAnalytics } from '../../actions/preload';
import { captureAnalyticsOncePerSession } from '../../actions/helpers/sessionAnalytics';
import csrfHeaders from '../../utils/csrfHeaders';
import buildRecordViewerPath from '../../util/buildRecordViewerPath';
import { formatPhoneNumber } from '../../containers/Voice/helpers';

import './referralReplyFaxApproval.scss';

export const REFERRAL_REPLY_FAX_APPROVAL_FEATURE = 'referral_reply_fax_approval';

export const replyFaxDestinationUrl = (sendToken) => (
  `/api/v1/receivers/${sendToken}/reply_fax_destination`
);

export const APPROVAL_SCOPES = [
  {
    scope: 'referral',
    label: 'This referral only',
    helper: 'Applies to this referral only.',
  },
  {
    scope: 'sender',
    label: 'This sender',
    helper: 'Saved for this sender. An admin can remove it anytime.',
  },
  {
    scope: 'sender_default',
    label: 'This sender\'s default',
    helper: 'Replaces this sender\'s default reply number. An admin can remove it anytime.',
  },
];

const DEFAULT_SCOPE = 'referral';

// A lookup that never answers must not hold Send forever: after this long the
// modal falls back to today's behavior (the server still refuses to send to an
// unapproved number).
export const REPLY_FAX_LOOKUP_TIMEOUT_MS = 8000;

const withTimeout = (promise, ms) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('reply_fax_lookup_timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

// What approving means depends on the scope picked. The backend has no field
// saying whether an automatic reply is held, so that line is conditional.
const PHI_NOTICES = {
  referral: 'Replies to this referral may send patient information (PHI) to this number. '
    + 'Only approve if it is correct for this referral, including any shared fax line. '
    + 'If an automatic reply is waiting, it will be sent to this number right away.',
  sender: 'Future replies to this sender may send patient information (PHI) to this number. '
    + 'Only approve if it is correct for this sender, including any shared fax line. '
    + 'If automatic replies are waiting for this sender, they will be sent to this number right away.',
  sender_default: 'This becomes the default reply number for this sender, so future replies may send '
    + 'patient information (PHI) to it. Only approve if it is correct for this sender, including any '
    + 'shared fax line. If automatic replies are waiting for this sender, they will be sent to this '
    + 'number right away.',
};

// The server lists the scopes this user may pick (`allowed_scopes`). Without
// that field, sender-wide scopes need a sender number to attach to; with no
// sender number only this referral applies. "This referral only" is always
// offered.
const availableScopesFor = (destination) => {
  const allowed = destination?.allowed_scopes;
  if (Array.isArray(allowed)) {
    return APPROVAL_SCOPES.filter(({ scope }) => scope === DEFAULT_SCOPE || allowed.includes(scope));
  }
  return destination?.sender_fax_number
    ? APPROVAL_SCOPES
    : APPROVAL_SCOPES.filter(({ scope }) => scope === DEFAULT_SCOPE);
};

const APPROVED_SCOPE_NOTES = {
  referral: 'Reply fax number approved for this referral only.',
  sender: 'Reply fax number approved for this sender.',
  sender_default: 'Reply fax number is this sender\'s default.',
};

const APPROVE = 'approve';
const KEEP_CURRENT = 'keep_current';

// Inline messages for the machine codes the server sends with a 403/422.
const APPROVAL_ERROR_CODES = {
  scope_not_allowed: 'Only an admin can save a number for this sender. Choose "This referral only" or ask your Admin.',
  invalid_scope: 'That option is not available for this referral. Choose another option and try again.',
  no_sender_fax_number: 'This fax has no sender number, so the number can only be approved for this referral.',
  invalid_sender: 'This fax has no sender number, so the number can only be approved for this referral.',
  sender_not_whitelisted: 'This sender has no default reply number to replace. Choose another option.',
  blacklisted: 'This number is blocked for your organization and cannot be used. Contact your Admin.',
};

const approvalErrorMessage = (error, keepingCurrent) => {
  const response = error?.response;
  if (!response) {
    // No answer from the server: the decision may or may not have been saved.
    return keepingCurrent
      ? 'Could not confirm keeping the current number. Check your connection and try again.'
      : 'Could not confirm the approval. Check your connection and try again.';
  }
  const { status, data } = response;
  const code = data?.error;
  if ((status === 422 || status === 403) && APPROVAL_ERROR_CODES[code]) {
    return APPROVAL_ERROR_CODES[code];
  }
  if (status === 422) {
    if (code === 'invalid_number') {
      return keepingCurrent
        ? 'The current number can no longer be kept. Close and reopen Reply to see the latest numbers.'
        : 'This number no longer matches the one found in the document. Close and reopen Reply to see the latest numbers.';
    }
    return keepingCurrent
      ? 'The current number cannot be kept. It may be invalid or blocked. Contact your Admin.'
      : 'This number cannot be approved. It may be invalid or blocked. Keep the current number or contact your Admin.';
  }
  if (status === 403) {
    return 'Approving reply fax numbers is not available for your organization.';
  }
  return keepingCurrent
    ? 'Could not keep the current number. The fax number was not changed. Please try again.'
    : 'Could not approve the new number. The fax number was not changed. Please try again.';
};

// Asks a person to approve a reply fax number found in the referral document
// before patient information is faxed to it. While the decision is pending the
// parent hides its reply form and this renders the decision step in its place.
// Renders nothing unless the org has `referral_reply_fax_approval` AND the
// reply_fax_destination endpoint answers with enabled:true — a missing
// endpoint, any request failure, or enabled:false leaves the reply modal
// exactly as it was once the lookup settles (onLookupSettled).
const ReferralReplyFaxApproval = ({
  sendToken,
  onApprovedDestination,
  onDecisionPendingChange,
  onLookupSettled,
  onApproved,
  lookupTimeoutMs,
}) => {
  const [destination, setDestination] = useState(null);
  const [decision, setDecision] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [selectedScope, setSelectedScope] = useState(DEFAULT_SCOPE);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  // Keep the latest callbacks without re-running the load effect when the parent re-renders.
  const callbacksRef = useRef({ onApprovedDestination, onDecisionPendingChange, onLookupSettled });
  useEffect(() => {
    callbacksRef.current = { onApprovedDestination, onDecisionPendingChange, onLookupSettled };
  });

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    if (!sendToken) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        // One deadline covers both requests, so Send is never held longer than the limit.
        const deadline = Date.now() + lookupTimeoutMs;
        const hasAccess = await withTimeout(
          verifyFeatureAccess(REFERRAL_REPLY_FAX_APPROVAL_FEATURE),
          lookupTimeoutMs,
        );
        if (cancelled || hasAccess !== true) return;
        const remainingMs = Math.max(0, deadline - Date.now());
        const { data } = await withTimeout(
          axios.get(replyFaxDestinationUrl(sendToken), { timeout: remainingMs || 1 }),
          remainingMs,
        );
        if (cancelled || !data || data.enabled !== true) return;

        setDestination(data);
        if (data.approval_state === 'pending') {
          callbacksRef.current.onDecisionPendingChange(true);
          captureAnalyticsOncePerSession('referral_reply_fax_approval_prompt_shown', {});
        } else if (data.approval_state === 'approved' && data.resolved_destination) {
          callbacksRef.current.onApprovedDestination(data.resolved_destination, { source: 'lookup' });
        }
      } catch (lookupError) {
        // No endpoint (404), no access, a network error or a timeout: the modal falls back
        // to today's behavior with no prompt, and the server still refuses to
        // send to an unapproved number. Counted once per session so a broken
        // lookup is visible without one event per modal open.
        if (!cancelled) {
          captureAnalyticsOncePerSession('referral_reply_fax_approval_lookup_failed', {
            status: lookupError?.response?.status || null,
          });
        }
      } finally {
        // Send stays blocked until here, so a fast click cannot skip the prompt.
        if (!cancelled) callbacksRef.current.onLookupSettled();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [sendToken, lookupTimeoutMs]);

  // Every decision is recorded server-side. "Keep current number" approves the
  // current destination for this referral only, which also releases a held
  // automatic reply to it.
  const submitDecision = async (decisionKey) => {
    if (submitting) return;
    const keepingCurrent = decisionKey === KEEP_CURRENT;
    if (!keepingCurrent && !confirmed) return;
    const faxNumber = keepingCurrent ? destination?.current_destination : destination?.extracted_number;
    if (!faxNumber) return;
    const scopeIsAvailable = availableScopesFor(destination).some(({ scope }) => scope === selectedScope);
    const scope = keepingCurrent || !scopeIsAvailable ? DEFAULT_SCOPE : selectedScope;

    setSubmitting(decisionKey);
    setError(null);
    captureAnalytics('referral_reply_fax_approval_decision', { decision: decisionKey, scope });

    try {
      const { data } = await axios.post(
        replyFaxDestinationUrl(sendToken),
        { scope, fax_number: faxNumber },
        { headers: csrfHeaders() },
      );
      if (!isMountedRef.current) return;
      if (!data || data.approval_state !== 'approved' || !data.resolved_destination) {
        if (!keepingCurrent) setConfirmed(false);
        setError(keepingCurrent
          ? 'The current number was not kept. The fax number was not changed. Please try again.'
          : 'The new number was not approved. The fax number was not changed. Please try again.');
        return;
      }
      setDestination(data);
      onApprovedDestination(data.resolved_destination, { source: 'decision' });
      onApproved();
      if (data.released_held_reply === true) {
        // The decision queued this referral's held automatic reply. The reply
        // form stays hidden: nothing on the server stops a manual reply from
        // going out alongside the queued one.
        setDecision('released');
        return;
      }
      setDecision(keepingCurrent ? 'kept' : 'approved');
      onDecisionPendingChange(false);
    } catch (requestError) {
      if (!isMountedRef.current) return;
      // A failed approval asks for the confirmation again.
      if (!keepingCurrent) setConfirmed(false);
      setError(approvalErrorMessage(requestError, keepingCurrent));
    } finally {
      if (isMountedRef.current) setSubmitting(null);
    }
  };

  // With no current number on file there is nothing to keep. Declining clears the
  // block without a server call, so the reply goes to whatever the user enters.
  const declineExtracted = () => {
    if (submitting) return;
    captureAnalytics('referral_reply_fax_approval_decision', { decision: 'declined', scope: null });
    setError(null);
    setDecision('declined');
    onDecisionPendingChange(false);
  };

  if (!destination) return null;

  const {
    approval_state: approvalState,
    approved_scope: approvedScope,
    current_destination: currentDestination,
    extracted_number: extractedNumber,
    evidence,
    file_token: fileToken,
    resolved_destination: resolvedDestination,
  } = destination;

  if (decision === 'released') {
    return (
      <div className="referral-reply-fax-approval__note" data-testid="reply-fax-released-note">
        {`The automatic reply for this referral was queued to ${formatPhoneNumber(resolvedDestination)}. You can close this window.`}
      </div>
    );
  }

  if (decision === 'kept') {
    return (
      <div className="referral-reply-fax-approval__note" data-testid="reply-fax-kept-note">
        {`Keeping the current number${resolvedDestination ? ` ${formatPhoneNumber(resolvedDestination)}` : ''}. The number found in the document was not approved.`}
      </div>
    );
  }

  if (decision === 'declined') {
    return (
      <div className="referral-reply-fax-approval__note" data-testid="reply-fax-declined-note">
        The number found in the document was not approved. Enter the fax number to reply to.
      </div>
    );
  }

  if (approvalState === 'approved' || decision === 'approved') {
    const note = APPROVED_SCOPE_NOTES[approvedScope] || 'Reply fax number approved.';
    return (
      <div className="referral-reply-fax-approval__note" data-testid="reply-fax-approved-note">
        {note}
      </div>
    );
  }

  if (approvalState !== 'pending') return null;

  const isSubmitting = Boolean(submitting);
  const availableScopes = availableScopesFor(destination);
  const scopeIsAvailable = availableScopes.some(({ scope }) => scope === selectedScope);
  const effectiveScope = scopeIsAvailable ? selectedScope : DEFAULT_SCOPE;
  const selectedHelper = APPROVAL_SCOPES.find(({ scope }) => scope === effectiveScope)?.helper;
  const phiNotice = PHI_NOTICES[effectiveScope];
  const pageNumber = evidence?.page_number;
  const supportingQuote = evidence?.supporting_quote;

  return (
    <div className="referral-reply-fax-approval" data-testid="reply-fax-approval-prompt">
      <div className="referral-reply-fax-approval__eyebrow">Reply destination</div>
      <h3 className="referral-reply-fax-approval__heading">Use the reply number on this fax?</h3>
      <p className="referral-reply-fax-approval__subtext">
        The document asks for replies at a different number.
      </p>

      <div className="referral-reply-fax-approval__numbers">
        <div className="referral-reply-fax-approval__card">
          <div className="referral-reply-fax-approval__card-label">Current reply number</div>
          <div className="referral-reply-fax-approval__card-number" data-testid="reply-fax-current-number">
            {currentDestination ? formatPhoneNumber(currentDestination) : 'None on file'}
          </div>
        </div>
        <span className="referral-reply-fax-approval__arrow" aria-hidden="true">→</span>
        <div className="referral-reply-fax-approval__card referral-reply-fax-approval__card--suggested">
          <div className="referral-reply-fax-approval__card-label">Suggested reply number</div>
          <div className="referral-reply-fax-approval__card-number" data-testid="reply-fax-extracted-number">
            {formatPhoneNumber(extractedNumber)}
          </div>
        </div>
      </div>

      {(supportingQuote || pageNumber) && (
        <div className="referral-reply-fax-approval__evidence">
          {supportingQuote && <blockquote>{`“${supportingQuote}”`}</blockquote>}
          <div className="referral-reply-fax-approval__source">
            {pageNumber ? `From this document · Page ${pageNumber}` : 'From this document'}
            {pageNumber && fileToken && (
              <>
                {' · '}
                {/* A new tab keeps this decision open while the approver reads the page. */}
                <a
                  href={buildRecordViewerPath({ file_token: fileToken, send_token: sendToken }, { mode: 'referred', page: pageNumber })}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reply-fax-evidence-page-link"
                  onClick={() => captureAnalytics('referral_reply_fax_evidence_page_opened', { page_number: pageNumber })}
                >
                  {`Open page ${pageNumber}`}
                </a>
              </>
            )}
          </div>
        </div>
      )}

      <p className="referral-reply-fax-approval__phi" data-testid="reply-fax-phi-notice">
        {phiNotice}
      </p>

      <div className="referral-reply-fax-approval__scope">
        <div className="referral-reply-fax-approval__scope-label" id="reply-fax-scope-label">
          Use this number for
        </div>
        <Radio.Group
          aria-labelledby="reply-fax-scope-label"
          value={effectiveScope}
          disabled={isSubmitting}
          onChange={(e) => {
            // The confirmation covers one scope; picking another asks again.
            setSelectedScope(e.target.value);
            setConfirmed(false);
          }}
        >
          {availableScopes.map(({ scope, label }) => (
            <Radio key={scope} value={scope}>{label}</Radio>
          ))}
        </Radio.Group>
      </div>

      <div className="referral-reply-fax-approval__confirm">
        <Checkbox
          checked={confirmed}
          disabled={isSubmitting}
          onChange={(e) => setConfirmed(e.target.checked)}
        >
          I verified this number and approve sending PHI to it.
        </Checkbox>
        <div className="referral-reply-fax-approval__helper" data-testid="reply-fax-scope-helper">
          {selectedHelper}
        </div>
      </div>

      <div className="referral-reply-fax-approval__actions">
        {currentDestination ? (
          <Button
            className="referral-reply-fax-approval__secondary"
            loading={submitting === KEEP_CURRENT}
            disabled={isSubmitting}
            onClick={() => submitDecision(KEEP_CURRENT)}
          >
            Keep current number
          </Button>
        ) : (
          <Button
            className="referral-reply-fax-approval__secondary"
            disabled={isSubmitting}
            onClick={declineExtracted}
          >
            Don&apos;t use this number
          </Button>
        )}
        <Button
          type="primary"
          className="referral-reply-fax-approval__primary"
          loading={submitting === APPROVE}
          disabled={!confirmed || isSubmitting}
          onClick={() => submitDecision(APPROVE)}
        >
          Approve &amp; continue
        </Button>
      </div>
      {error && (
        <div className="referral-reply-fax-approval__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

ReferralReplyFaxApproval.propTypes = {
  sendToken: PropTypes.string,
  onApprovedDestination: PropTypes.func.isRequired,
  onDecisionPendingChange: PropTypes.func,
  onLookupSettled: PropTypes.func,
  onApproved: PropTypes.func,
  lookupTimeoutMs: PropTypes.number,
};

ReferralReplyFaxApproval.defaultProps = {
  sendToken: null,
  onDecisionPendingChange: () => {},
  onLookupSettled: () => {},
  onApproved: () => {},
  lookupTimeoutMs: REPLY_FAX_LOOKUP_TIMEOUT_MS,
};

export default ReferralReplyFaxApproval;
